import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSidebar } from "../../contexts/SidebarContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useAddPatient } from "../../contexts/AddPatientContext";
import { useBookAppointment } from "../../contexts/BookAppointmentContext";
import { useClinic } from "../../contexts/ClinicContext";
import ClinicSwitcher from "./ClinicSwitcher";
import {
  GridIcon,
  AnalyticsIcon,
  CalendarIcon,
  UsersIcon,
  StethoscopeIcon,
  CapsuleIcon,
  VaccineShieldIcon,
  PlusIcon,
  SettingsIcon,
  ChevronDownIcon,
  PanelLeftIcon,
  HamburgerIcon,
  ReceiptIcon,
} from "../icons";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import SyncStatusBadge from "../SyncStatusBadge";
import { SyncErrorBadge } from "../sync/SyncErrorBadge";
import { CredentialRequiredModal } from "../CredentialRequiredModal";
import { useRole, UserRole } from "../../hooks/useRole";
import kaagazyLockup from "../../assets/kaagazy-lockup.png";
import kaagazySymbol from "../../assets/kaagazy-symbol.png";
import { SettingsAPI } from "../../services/settings.service";

// Clinic name shown at the top of the sidebar. Configurable via env so it can be
// branded per deployment.
const CLINIC_NAME = process.env.REACT_APP_CLINIC_NAME || "Kaagazy Clinic";

// Module-level cache — survives SPA navigation (Sidebar re-mounts per page).
// Populated on first successful getProfile(); subsequent mounts read instantly.
let _cachedClinicName: string | null = null;
let _cachedProfilePhoto: string | null = null;

interface NavItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  path?: string;
  count?: number;
}

const navItems: NavItem[] = [
  { id: "dashboard",    icon: <GridIcon />,        label: "Dashboard",    path: "/dashboard" },
  { id: "diagnosis",    icon: <AnalyticsIcon />,   label: "Diagnosis",    path: "/start-visit" },
  { id: "appointments", icon: <CalendarIcon />,    label: "Appointments", path: "/appointments" },
  { id: "patients",     icon: <UsersIcon />,       label: "Patients",     path: "/patients" },
  { id: "doctors",      icon: <StethoscopeIcon />, label: "Doctors",      path: "/doctors" },
  { id: "pharmacy",     icon: <CapsuleIcon />,     label: "Pharmacy",     path: "/pharmacy" },
  { id: "billing",      icon: <ReceiptIcon />,     label: "Billing",      path: "/billing" },
  { id: "immunizations", icon: <VaccineShieldIcon />, label: "Vaccinations", path: "/immunizations/cohort" },
];

// Colourful quick-add menu — mirrors the dashboard's "Add New" card so the plus
// button and the dashboard share one visual language. `accent` is the icon-tile
// background colour.
const plusMenuItems = [
  {
    id: "add-patient",
    label: "New Patient",
    accent: "bg-blue-500",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M19 8v6M22 11h-6" />
      </svg>
    ),
  },
  {
    id: "create-appointment",
    label: "Book Appointment",
    accent: "bg-fuchsia-500",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
      </svg>
    ),
  },
  {
    id: "new-prescription",
    label: "New Prescription",
    accent: "bg-green-500",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
        <path d="m8.5 8.5 7 7" />
      </svg>
    ),
  },
  {
    id: "lab-results",
    label: "Lab Results",
    accent: "bg-orange-500",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6M9 13h6M9 17h6" />
      </svg>
    ),
  },
];

// ── Role-based visibility ─────────────────────────────────────────────────────
// Declare which nav item IDs are hidden per role.  Admins see everything.
// An empty set means "hide nothing for this role".

// "doctors" is not listed here any more: Doctor Management follows the View
// Doctors grant (see canViewDoctors below), so a custom role can be given it.
const HIDDEN_NAV_ITEMS_BY_ROLE: Partial<Record<UserRole, Set<string>>> = {
  // Receptionists cannot access the visit/diagnosis pad.
  // Also hide Vaccinations - the cohort/bulk-remind endpoints are
  // Admin/Doctor-only server-side (require_roles), so the nav link would
  // just 403 for a Receptionist.
  Receptionist: new Set(["diagnosis", "immunizations"]),
  // PlatformAdmin is cross-tenant SwasthiQ staff with NO patient/clinical access
  // (see Role Access Matrix). It only reviews doctor KYC — hide the clinic nav.
  PlatformAdmin: new Set(["dashboard", "diagnosis", "appointments", "patients", "doctors", "pharmacy", "billing", "immunizations"]),
};

// Quick-add items (in order): add-patient, create-appointment, new-prescription, lab-results.
const HIDDEN_PLUS_ITEMS_BY_ROLE: Partial<Record<UserRole, Set<string>>> = {
  // Admin: New Patient + Book Appointment only.
  Admin: new Set(["new-prescription", "lab-results"]),
  // Doctor: New Patient + Book Appointment + New Prescription.
  Doctor: new Set(["lab-results"]),
  // Receptionist: New Patient + Book Appointment only (no visit/clinical context).
  Receptionist: new Set(["new-prescription", "lab-results"]),
  // PlatformAdmin has no patient/clinical access — no quick-add actions at all.
  PlatformAdmin: new Set(["add-patient", "create-appointment", "new-prescription", "lab-results"]),
};

// Whether the Settings link at the bottom should be hidden.
const HIDE_SETTINGS_FOR: Set<UserRole> = new Set();   // visible to all for now

export const Sidebar: React.FC = () => {
  const { isExpanded, setIsExpanded } = useSidebar();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isPlusMenuOpen, setIsPlusMenuOpen] = useState(false);
  // Shown when a Doctor without a platform-verified medical registration clicks
  // "Diagnosis" — the same credential prompt as the patient page's Start Visit CTA.
  const [showCredentialModal, setShowCredentialModal] = useState(false);
  const [plusMenuPos, setPlusMenuPos] = useState({ top: 0, left: 0 });
  // Signed-in user's profile photo (presigned GET URL). Falls back to the
  // SwasthiQ symbol when the user hasn't uploaded one.
  const [profilePhoto, setProfilePhoto] = useState<string | null>(_cachedProfilePhoto);
  const [clinicName, setClinicName] = useState(_cachedClinicName ?? CLINIC_NAME);
  const desktopPlusBtnRef = useRef<HTMLButtonElement>(null);
  const mobilePlusBtnRef  = useRef<HTMLButtonElement>(null);
  const plusMenuRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { openAddPatientModal }       = useAddPatient();
  const { openBookAppointmentModal }  = useBookAppointment();

  // The header must name the clinic currently IN SCOPE, not the one on the
  // user's own row. An owner working in clinic B seeing clinic A's name above
  // clinic B's patients is the mislabelled-data failure this feature exists to
  // avoid. Falls back to the profile-derived name for single-clinic users,
  // who have no org context.
  const { activeClinic } = useClinic();
  const displayClinicName = activeClinic?.name ?? clinicName;

  // Role-based filtering — isLoaded is false only on the very first render before
  // the localStorage cache is read; in practice this resolves synchronously.
  const { role, isPlatformAdmin, isDoctor, isAdmin, can_prescribe, isLoaded, canOr } = useRole();
  const hiddenNavIds   = HIDDEN_NAV_ITEMS_BY_ROLE[role]  ?? new Set<string>();
  const hiddenPlusIds  = HIDDEN_PLUS_ITEMS_BY_ROLE[role] ?? new Set<string>();
  // Vaccinations is a clinical-sign-off gate (not a payment gate) - off for
  // every clinic until the schedule data is confirmed, independent of role.
  const { isFeatureAccessible } = useFeatureFlags();
  const immunizationEnabled = isFeatureAccessible("immunization");

  // RBAC grants behind the two nav entries whose pages are permission-gated in
  // App.tsx, and the two quick actions that perform a gated write. Legacy fallback
  // is `true` for all four: every clinic role could reach them before RBAC, so a
  // backend that sends no grants must leave the nav exactly as it was. Hiding
  // these is cosmetic — RequirePermission blocks the route and the endpoint 403s.
  const canViewPatients     = canOr("patients.view", true);
  const canViewAppointments = canOr("appointments.view", true);
  const canCreatePatient    = canOr("patients.create", true);
  const canBookAppointment  = canOr("appointments.create", true);
  // Same idea for the areas whose routes are now permission-gated too. Legacy
  // fallbacks reproduce the old role rules exactly: Doctors was Admin-only,
  // Diagnosis was Admin/Doctor (Receptionist is still hidden by role above),
  // Billing was every clinic role.
  const canViewDoctors      = canOr("doctors.view", isAdmin);
  const canWriteVisits      = canOr("visits.write", isAdmin || isDoctor);
  const canPrescribe        = canOr("prescriptions.write", isAdmin || isDoctor);
  const canCreateBill       = canOr("billing.write", true);

  // A Doctor/Admin whose medical registration isn't platform-verified yet cannot
  // prescribe. Gate on isLoaded so a warm-but-still-revalidating session doesn't
  // briefly treat a verified clinician as unverified.
  const unverifiedClinician = isLoaded && !can_prescribe;
  // Admin without a verified registration: hide Diagnosis entirely until verified.
  const hideDiagnosisForAdmin = isAdmin && unverifiedClinician;
  // Doctor without a verified registration: keep Diagnosis visible but intercept
  // the click to show the credential modal instead of opening the diagnosis pad.
  const gateDiagnosisForDoctor = isDoctor && unverifiedClinician;

  const visibleNavItems  = useMemo(
    () => navItems.filter((item) => {
      if (hiddenNavIds.has(item.id)) return false;
      if (item.id === "diagnosis" && (hideDiagnosisForAdmin || !canWriteVisits)) return false;
      if (item.id === "patients" && !canViewPatients) return false;
      if (item.id === "appointments" && !canViewAppointments) return false;
      if (item.id === "doctors" && !canViewDoctors) return false;
      if (item.id === "billing" && !canCreateBill) return false;
      if (item.id === "immunizations" && !immunizationEnabled) return false;
      return true;
    }),
    [role, hideDiagnosisForAdmin, canViewPatients, canViewAppointments, canViewDoctors, canWriteVisits, canCreateBill, immunizationEnabled], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const visiblePlusItems = useMemo(
    () => plusMenuItems.filter((item) => {
      if (hiddenPlusIds.has(item.id)) return false;
      if (item.id === "add-patient" && !canCreatePatient) return false;
      if (item.id === "create-appointment" && !canBookAppointment) return false;
      // Opens the visit pad (/start-visit), which is gated on visits.write.
      if (item.id === "new-prescription" && !(canPrescribe && canWriteVisits)) return false;
      // "Lab results" only navigates to /patients — pointless without the grant.
      if (item.id === "lab-results" && !canViewPatients) return false;
      return true;
    }),
    [role, canCreatePatient, canBookAppointment, canViewPatients, canPrescribe, canWriteVisits], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const showSettings   = !HIDE_SETTINGS_FOR.has(role);

  // Derive active item from current path
  const activeItem = useMemo(() => {
    const p = location.pathname;
    if (p.startsWith("/patients"))    return "patients";
    if (p.startsWith("/doctors") || p.startsWith("/roster")) return "doctors";
    if (p.startsWith("/start-visit") || p.startsWith("/90sdiagnosis") || p.startsWith("/soap") || p.startsWith("/continue-diagnosis")) return "diagnosis";
    if (p.startsWith("/appointments")) return "appointments";
    if (p.startsWith("/pharmacy")) return "pharmacy";
    if (p.startsWith("/immunizations")) return "immunizations";
    if (p.startsWith("/billing") || p.startsWith("/accounting")) return "billing";
    if (p === "/" || p === "/dashboard") return "dashboard";
    if (p.startsWith("/settings")) return "settings";
    return "";
  }, [location.pathname]);

  // Live width of the desktop sidebar (collapsed 72px → expanded 240px).
  const desktopSidebarWidth = isExpanded ? 240 : 72;

  // Publish the live width so every page's content padding can re-flow with the
  // sidebar instead of being overlaid by it. See --sidebar-w in index.css.
  useEffect(() => {
    document.documentElement.style.setProperty("--sidebar-w", `${desktopSidebarWidth}px`);
  }, [desktopSidebarWidth]);

  // Load the signed-in user's profile photo once for the header avatar.
  // Non-critical: on failure the avatar just falls back to the symbol.
  useEffect(() => {
    SettingsAPI.getProfile()
      .then((p) => {
        const photo = p.photo_url ?? null;
        const name = p.clinic_name || null;
        _cachedProfilePhoto = photo;
        if (name) _cachedClinicName = name;
        setProfilePhoto(photo);
        if (name) setClinicName(name);
      })
      .catch(() => { /* keep fallbacks */ });
  }, []);

  // Close plus menu on outside click
  useEffect(() => {
    if (!isPlusMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        plusMenuRef.current && !plusMenuRef.current.contains(e.target as Node) &&
        desktopPlusBtnRef.current && !desktopPlusBtnRef.current.contains(e.target as Node) &&
        mobilePlusBtnRef.current && !mobilePlusBtnRef.current.contains(e.target as Node)
      ) {
        setIsPlusMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isPlusMenuOpen]);

  const openPlusMenu = (btnRef: React.RefObject<HTMLButtonElement | null>) => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const isDesktop = window.innerWidth >= 1024;
    if (isDesktop) {
      const sidebarW = isExpanded ? 240 : 72;
      const popupH   = 248;
      const top = Math.min(
        Math.max(rect.top + rect.height / 2 - popupH / 2, 16),
        window.innerHeight - popupH - 16
      );
      setPlusMenuPos({ top, left: sidebarW + 12 });
    } else {
      setPlusMenuPos({ top: window.innerHeight / 2 - 130, left: window.innerWidth / 2 - 144 });
    }
    setIsPlusMenuOpen((prev) => !prev);
  };

  const handlePlusMenuItemClick = (id: string) => {
    // Re-check rather than trusting the filtered menu: a grant can be revoked
    // while the menu is open.
    if (id === "add-patient" && canCreatePatient)        openAddPatientModal();
    if (id === "create-appointment" && canBookAppointment) openBookAppointmentModal();
    if (id === "new-prescription" && canPrescribe && canWriteVisits) navigate("/start-visit");
    if (id === "lab-results")         navigate("/patients");
    setIsPlusMenuOpen(false);
    setIsMobileOpen(false);
  };

  const handleNavItemClick = (item: NavItem) => {
    // Unverified Doctor tapping Diagnosis → show the credential modal instead of
    // navigating to the diagnosis pad (Admins never reach here — the entry is hidden).
    if (item.id === "diagnosis" && gateDiagnosisForDoctor) {
      setShowCredentialModal(true);
      setIsMobileOpen(false);
      return;
    }
    if (item.path) navigate(item.path);
    setIsMobileOpen(false);
  };

  const SidebarInner = ({
    expanded,
    plusBtnRef,
  }: {
    expanded: boolean;
    plusBtnRef: React.RefObject<HTMLButtonElement | null>;
  }) => (
    <div className="flex h-full flex-col">
      {/* Header / Logo — `relative` anchors the clinic-switcher dropdown. */}
      <div className="relative flex items-center justify-between px-4 py-4 min-h-[64px]">
        {expanded ? (
          <>
            <div className="flex items-center gap-2 min-w-0">
              <span className="flex-shrink-0 h-8 w-8 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center ring-1 ring-slate-200">
                <img
                  src={profilePhoto || kaagazySymbol}
                  alt={displayClinicName}
                  className="h-full w-full object-cover"
                  onError={(e) => { e.currentTarget.src = kaagazySymbol; }}
                />
              </span>
              <span className="font-semibold text-slate-900 text-[15px] whitespace-normal break-words leading-snug" title={displayClinicName}>{displayClinicName}</span>
              {/* Renders nothing unless the caller is an org owner with >1 clinic. */}
              <ClinicSwitcher />

            </div>
            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
              {/* Universal search removed — feature not active yet. */}
              <button
                onClick={() => setIsExpanded(false)}
                className="text-slate-500 hover:text-slate-800 transition-colors p-1 rounded-md hover:bg-slate-100"
                aria-label="Collapse sidebar"
              >
                <PanelLeftIcon />
              </button>
            </div>
          </>
        ) : (
          <button
            onClick={() => setIsExpanded(true)}
            className="mx-auto text-slate-500 hover:text-slate-800 transition-colors p-1 rounded-md hover:bg-slate-100"
            aria-label="Expand sidebar"
          >
            <PanelLeftIcon />
          </button>
        )}
      </div>

      {/* Nav items */}
      <nav className="flex flex-col gap-0.5 px-3 flex-1 overflow-y-auto">
        {isPlatformAdmin ? (
          /* PlatformAdmin is cross-tenant SwasthiQ staff with no clinic access —
             the only tool they see is the doctor KYC review queue. */
          <button
            onClick={() => { navigate("/platform/kyc"); setIsMobileOpen(false); }}
            title={!expanded ? "KYC Review" : undefined}
            className={`flex items-center gap-3 w-full rounded-lg px-3 py-2.5 transition-all text-left
              ${location.pathname.startsWith("/platform/kyc") ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}
              ${!expanded ? "justify-center" : ""}
            `}
          >
            <span className="flex-shrink-0 text-slate-500">
              <svg viewBox="0 0 20 20" className="w-5 h-5 fill-current">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </span>
            {expanded && (
              <span className="flex-1 text-sm font-semibold text-slate-900 truncate">KYC Review</span>
            )}
          </button>
        ) : (
          <>
            {visibleNavItems.map((item) => {
              const isActive = activeItem === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavItemClick(item)}
                  title={!expanded ? item.label : undefined}
                  className={`flex items-center gap-3 w-full rounded-lg px-3 py-2.5 transition-all text-left
                    ${isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}
                    ${!expanded ? "justify-center" : ""}
                  `}
                >
                  <span className={`flex-shrink-0 ${isActive ? "text-indigo-600" : "text-slate-500"}`}>
                    {item.icon}
                  </span>
                  {expanded && (
                    <>
                      <span className={`flex-1 text-sm truncate ${isActive ? "font-semibold text-slate-900" : "font-normal"}`}>
                        {item.label}
                      </span>
                      {item.count !== undefined && (
                        <span className="flex items-center gap-0.5 text-slate-400 text-xs flex-shrink-0">
                          {item.count}<ChevronDownIcon />
                        </span>
                      )}
                    </>
                  )}
                </button>
              );
            })}

            {/* Plus / Add button — hidden when the role has no quick-add actions (e.g. PlatformAdmin) */}
            {visiblePlusItems.length > 0 && (
              <button
                ref={plusBtnRef}
                onClick={() => openPlusMenu(plusBtnRef)}
                title={!expanded ? "Add New" : undefined}
                className={`mt-1 flex items-center justify-center rounded-xl border border-slate-200 bg-white py-3 w-full text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition-all shadow-sm ${
                  !expanded ? "h-12 w-12 mx-auto" : ""
                } ${isPlusMenuOpen ? "border-indigo-300 bg-indigo-50" : ""}`}
              >
                <span className={`${isPlusMenuOpen ? "text-indigo-600" : "text-slate-600"}`}>
                  <PlusIcon />
                </span>
              </button>
            )}

            {/* Kaagazy AI button hidden for now — feature not yet released. */}
          </>
        )}
      </nav>

      {/* Bottom block: product branding, Settings, then the sync status pinned
          to the very bottom (separated from Settings by a gap). Two sync badges:
            - SyncStatusBadge: server → client pull progress (syncing / synced / offline).
            - SyncErrorBadge:  client → server push queue health (pending / failed / conflict).
          Hidden entirely for PlatformAdmin — cross-tenant staff have no clinic
          sync or settings. */}
      {!isPlatformAdmin && (
        <div className="px-3 pb-6 mt-auto">
          {/* Product branding (expanded sidebar only) */}
          {expanded && (
            <div className="px-2 pt-3 mb-2 border-t border-slate-100">
              <img
                src={kaagazyLockup}
                alt="Kaagazy by SwasthiQ"
                className="h-9 w-auto object-contain"
              />
            </div>
          )}

          {showSettings && (
            <button
              title={!expanded ? "Settings" : undefined}
              onClick={()=>navigate("/settings")}
              className={`mt-4 flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-all text-left ${activeItem === "settings" ? "bg-slate-100 text-slate-900" : "text-slate-500 hover:bg-slate-50 hover:text-slate-900" } ${!expanded ? "justify-center" : ""}`}
            >
              <span className="flex-shrink-0"><SettingsIcon /></span>
              {expanded && <span className="text-sm font-normal">Settings</span>}
            </button>
          )}

          {/* Sync status — pinned at the very bottom, spaced apart from Settings */}
          <div className="mt-6 space-y-2">
            <SyncStatusBadge expanded={expanded} className="w-full" />
            <SyncErrorBadge expanded={expanded} />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="fixed left-4 z-40 flex h-10 w-10 items-center justify-center rounded-lg bg-white text-slate-600 shadow-md lg:hidden"
        style={{ top: "calc(1rem + var(--offline-banner-h, 0px))" }}
        aria-label="Open menu"
      >
        <HamburgerIcon />
      </button>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => { setIsMobileOpen(false); setIsPlusMenuOpen(false); }}
        />
      )}

      {/* Mobile/tablet drawer */}
      <aside
        className={`fixed left-0 z-50 bg-white transition-transform duration-300 ease-in-out lg:hidden ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{
          width: 240,
          top: "var(--offline-banner-h, 0px)",
          height: "calc(100% - var(--offline-banner-h, 0px))",
          boxShadow: "4px 0 24px 0 rgba(0,0,0,0.10)",
        }}
      >
        <SidebarInner expanded={true} plusBtnRef={mobilePlusBtnRef} />
      </aside>

      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex fixed left-0 bg-white flex-col z-30 transition-all duration-300 ease-in-out"
        style={{
          width: desktopSidebarWidth,
          top: "var(--offline-banner-h, 0px)",
          height: "calc(100% - var(--offline-banner-h, 0px))",
          boxShadow: "2px 0 16px 0 rgba(0,0,0,0.06)",
        }}
      >
        <SidebarInner expanded={isExpanded} plusBtnRef={desktopPlusBtnRef} />
      </aside>

      {/* Plus menu popup */}
      {isPlusMenuOpen && (
        <>
          <div className="fixed inset-0 z-[60] lg:hidden" onClick={() => setIsPlusMenuOpen(false)} />
          <div
            ref={plusMenuRef}
            className="fixed z-[70] w-72 bg-white rounded-2xl overflow-hidden"
            style={{
              top: plusMenuPos.top,
              left: plusMenuPos.left,
              boxShadow: "0 8px 40px 0 rgba(0,0,0,0.14), 0 2px 8px 0 rgba(0,0,0,0.08)",
            }}
          >
            {visiblePlusItems.map((item, index) => (
              <React.Fragment key={item.id}>
                <button
                  onClick={() => handlePlusMenuItemClick(item.id)}
                  className="flex w-full items-center gap-4 px-5 py-4 text-left hover:bg-slate-50 active:bg-slate-100 transition-colors"
                >
                  <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-white ${item.accent}`}>
                    {item.icon}
                  </span>
                  <span className="text-[15px] font-medium text-slate-800">{item.label}</span>
                </button>
                {index < visiblePlusItems.length - 1 && <div className="mx-5 border-t border-slate-100" />}
              </React.Fragment>
            ))}
          </div>
        </>
      )}

      {/* Credential-required modal — same prompt as the patient page's Start Visit
          CTA, shown when an unverified Doctor clicks Diagnosis. */}
      <CredentialRequiredModal
        isOpen={showCredentialModal}
        onClose={() => setShowCredentialModal(false)}
      />
    </>
  );
};

export default Sidebar;
